import React, {useEffect, useState} from "react";
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Alert,
    Button,
    CardContent,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import FormFieldLayout from "../components/layout/FormFieldLayout";
import FormControlsLayout from "../components/layout/FormControlsLayout";
import PageLayout from "../components/layout/PageLayout";
import {useTranslation} from "react-i18next";
import {LOCALE_LABEL_KEY_BY_CODE, normalizeSupportedLocale, SUPPORTED_LOCALES} from "../constants/locales";

const RegisterPage = () => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [name, setName] = useState("");
    const [locale, setLocale] = useState(normalizeSupportedLocale(i18n.language, "en"));
    const [oauthClients, setOauthClients] = useState(null);
    const [error, setError] = useState(null);
    const [emailError, setEmailError] = useState(null);
    const [passwordError, setPasswordError] = useState(null);
    const [confirmPasswordError, setConfirmPasswordError] = useState(null);
    const [nameError, setNameError] = useState(null);
    const [localeError, setLocaleError] = useState(null);
    const [loading, setLoading] = useState(false);

    const clearFieldErrors = () => {
        setEmailError(null);
        setPasswordError(null);
        setConfirmPasswordError(null);
        setNameError(null);
        setLocaleError(null);
    };

    useEffect(() => {
        fetch('/api/security/oauth/clients', {
            headers: {
                "Content-Type": "application/json"
            },
            method: 'GET',
        })
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }
                return Promise.reject(response);
            })
            .then((response) => {
                setOauthClients(response);
            })
            .catch(() => {
                setOauthClients(null);
            });
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        clearFieldErrors();

        const trimmedEmail = email.trim();
        const trimmedName = name.trim();
        const trimmedPassword = password.trim();
        const trimmedConfirm = confirmPassword.trim();

        let invalid = false;
        if (!trimmedEmail) {
            setEmailError(t("auth.register.errors.emailRequired"));
            invalid = true;
        }
        if (!trimmedName) {
            setNameError(t("auth.register.errors.usernameRequired"));
            invalid = true;
        }
        if (!trimmedPassword) {
            setPasswordError(t("auth.register.errors.passwordRequired"));
            invalid = true;
        }
        if (!trimmedConfirm) {
            setConfirmPasswordError(t("auth.register.errors.confirmPasswordRequired"));
            invalid = true;
        }
        if (!SUPPORTED_LOCALES.includes(locale)) {
            setLocaleError(t("auth.register.errors.localeRequired"));
            invalid = true;
        }
        if (invalid) {
            return;
        }
        if (trimmedPassword !== trimmedConfirm) {
            const mismatchMsg = t("auth.register.errors.passwordMismatch");
            setPasswordError(mismatchMsg);
            setConfirmPasswordError(mismatchMsg);
            return;
        }
        if (trimmedPassword.length < 4) {
            setError(t("auth.register.errors.passwordTooShort"));
            return;
        }

        setLoading(true);
        // RegisterRq: only one password hash is stored; confirm is validated on the client only.
        fetch('/api/RegisterRq', {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email: trimmedEmail,
                name: trimmedName,
                password: trimmedPassword,
                createAccount: true,
                locale: normalizeSupportedLocale(locale, "en"),
            }),
        })
            .then((res) => res.json())
            .then((body) => {
                clearFieldErrors();
                if (body.status === 'OK') {
                    navigate('/signin', { state: { message: t("auth.register.successMessage") } });
                } else if (body.status === 'EMAIL_USED') {
                    setError(t("auth.register.errors.emailUsed"));
                } else {
                    setError(t("auth.register.errors.registrationFailed"));
                }
            })
            .catch(() => {
                clearFieldErrors();
                setError(t("auth.register.errors.generic"));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <PageLayout
            title={t("auth.register.title")}
            showMenuButton={false}
            showNavigationMenu={false}
            cardStyle={{ marginLeft: 330, marginTop: 10, marginRight: 10, marginBottom: 10 }}
        >
            <CardContent>
                <form noValidate onSubmit={handleSubmit}>
                    {error && (
                        <FormFieldLayout>
                            <Alert severity="error" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        </FormFieldLayout>
                    )}
                    <FormFieldLayout>
                        <TextField
                            required
                            fullWidth
                            label={t("auth.register.email")}
                            variant="standard"
                            sx={{ width: 300 }}
                            type="email"
                            value={email}
                            error={Boolean(emailError)}
                            helperText={emailError || undefined}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setError(null);
                                setEmailError(null);
                            }}
                            disabled={loading}
                        />
                    </FormFieldLayout>
                    <FormFieldLayout>
                        <TextField
                            required
                            fullWidth
                            label={t("auth.register.password")}
                            variant="standard"
                            sx={{ width: 300 }}
                            type="password"
                            value={password}
                            error={Boolean(passwordError)}
                            helperText={passwordError || undefined}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setError(null);
                                setPasswordError(null);
                                setConfirmPasswordError(null);
                            }}
                            disabled={loading}
                        />
                    </FormFieldLayout>
                    <FormFieldLayout>
                        <TextField
                            required
                            fullWidth
                            label={t("auth.register.confirmPassword")}
                            variant="standard"
                            sx={{ width: 300 }}
                            type="password"
                            value={confirmPassword}
                            error={Boolean(confirmPasswordError)}
                            helperText={confirmPasswordError || undefined}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setError(null);
                                setConfirmPasswordError(null);
                                setPasswordError(null);
                            }}
                            disabled={loading}
                        />
                    </FormFieldLayout>
                    <FormFieldLayout>
                        <TextField
                            required
                            fullWidth
                            label={t("auth.register.username")}
                            variant="standard"
                            sx={{ width: 300 }}
                            type="text"
                            value={name}
                            error={Boolean(nameError)}
                            helperText={nameError || undefined}
                            onChange={(e) => {
                                setName(e.target.value);
                                setError(null);
                                setNameError(null);
                            }}
                            disabled={loading}
                        />
                    </FormFieldLayout>
                    <FormFieldLayout>
                        <FormControl
                            variant="standard"
                            sx={{ width: 300 }}
                            required
                            disabled={loading}
                            error={Boolean(localeError)}
                        >
                            <InputLabel id="register-locale-label">{t("auth.register.locale")}</InputLabel>
                            <Select
                                labelId="register-locale-label"
                                value={locale}
                                onChange={(e) => {
                                    const nextLocale = e.target.value;
                                    setLocale(nextLocale);
                                    setError(null);
                                    setLocaleError(null);
                                    i18n.changeLanguage(nextLocale);
                                }}
                                label={t("auth.register.locale")}
                            >
                                {SUPPORTED_LOCALES.map((localeCode) => (
                                    <MenuItem key={localeCode} value={localeCode}>
                                        {t(LOCALE_LABEL_KEY_BY_CODE[localeCode])}
                                    </MenuItem>
                                ))}
                            </Select>
                            {localeError ? <FormHelperText>{localeError}</FormHelperText> : null}
                        </FormControl>
                    </FormFieldLayout>
                    {oauthClients &&
                        <FormFieldLayout>
                            {oauthClients.map(client =>
                                <FormFieldLayout key={client.name}><a href={client.url}>{client.name}</a></FormFieldLayout>)}
                        </FormFieldLayout>
                    }
                    <FormControlsLayout>
                        <Button type="submit" variant="contained" disabled={loading}>
                            {loading ? t("auth.register.signingUp") : t("auth.register.signUp")}
                        </Button>
                        <Button variant="outlined" component={RouterLink} to="/recovery" disabled={loading}>
                            {t("auth.register.passwordRecovery")}
                        </Button>
                        <Button variant="outlined" component={RouterLink} to="/signin" disabled={loading}>
                            {t("auth.register.signIn")}
                        </Button>
                    </FormControlsLayout>
                </form>
            </CardContent>
        </PageLayout>
    );
};

export default RegisterPage;
